import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Wrench,
  ImageIcon,
  Loader2,
  Pencil,
  AlertTriangle,
  ExternalLink,
  FileText,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  serviceCenterRepairsApi,
  type RemontEntry,
  type Vendor,
} from '@/api/service-center-repairs';
import { LinkText } from '@/components/LinkText';
import { RemontSheet } from '@/features/legacy/components/RemontSheet';
import { MigratedCrudHistory } from '@/components/MigratedCrudHistory';

interface RemontDetailSheetProps {
  entry: RemontEntry | null;
  vendors: Vendor[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const FIELD_LABELS: Record<string, string> = {
  clientId: 'Клиент',
  clientName: 'Клиент',
  deviceName: 'Устройство',
  deviceOwner: 'Чье устройство',
  complaint: 'Неисправность',
  vendorId: 'Сервис-центр',
  legacyVendorId: 'Legacy СЦ',
  photoPath: 'Фото',
  legacyPhotoPath: 'Legacy фото',
  deliveredToService: 'Отвез в СЦ',
  pickedUpFromService: 'Забрал из СЦ',
  documentsReceivedFromService: 'Документы от СЦ',
  billedToClient: 'Выставлено клиенту',
  paidToService: 'Оплачено СЦ',
  cost: 'Стоимость',
  estimatedVendorAmount: 'Предварительная стоимость СЦ',
  vendorAmount: 'Сумма счёта СЦ',
  calculatedClientTotal: 'Итого клиенту',
  lastActivityAt: 'Последнее движение',
  kind: 'Тип файла',
  originalName: 'Файл',
  serviceAnswer: 'Ответ СЦ',
  isArchived: 'Архив',
  archivedAt: 'Дата архивации',
  createdById: 'Создал',
  updatedById: 'Изменил',
  legacyId: 'Legacy ID',
  legacyUserlistId: 'Legacy пользователь',
  legacyCreatedByName: 'Legacy автор',
  legacyUsername: 'Legacy логин',
  legacyCreatedAt: 'Legacy создано',
  legacyLastChange: 'Legacy изменение',
  legacyLog: 'Legacy лог',
  importedAt: 'Импортировано',
  createdAt: 'Создано',
  updatedAt: 'Изменено',
};

const ATTACHMENT_LABELS = {
  DEVICE_BEFORE: 'Фото до ремонта',
  DEVICE_AFTER: 'Фото после ремонта',
  SERVICE_INVOICE: 'Счёт СЦ',
  SERVICE_ACT: 'Акт СЦ',
  OTHER: 'Другой файл',
} as const;

async function openAttachment(repairId: string, attachmentId: string) {
  const url = await serviceCenterRepairsApi.getAttachmentBlobUrl(
    repairId,
    attachmentId,
  );
  window.open(url, '_blank', 'noopener,noreferrer');
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

function AuthImage({ filePath, alt }: { filePath: string; alt: string }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    async function fetchImage() {
      try {
        setLoading(true);
        setError(false);
        const url = await serviceCenterRepairsApi.getPhotoBlobUrl(filePath);
        if (!cancelled) {
          objectUrl = url;
          setBlobUrl(url);
        }
      } catch {
        if (!cancelled) {
          setError(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchImage();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [filePath]);

  if (loading) {
    return (
      <div className="w-20 h-20 rounded border bg-muted flex items-center justify-center">
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !blobUrl) {
    return (
      <div className="w-20 h-20 rounded border bg-muted flex items-center justify-center">
        <ImageIcon className="w-4 h-4 text-muted-foreground" />
      </div>
    );
  }

  return (
    <a href={blobUrl} target="_blank" rel="noopener noreferrer">
      <img
        src={blobUrl}
        alt={alt}
        className="w-20 h-20 object-cover rounded border hover:opacity-80 transition-opacity"
      />
    </a>
  );
}

function StatusRow({ label, value, isYes }: { label: string; value: string; isYes?: boolean }) {
  return (
    <div className="flex justify-between text-sm px-3 py-2 border-b border-border last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className={isYes === false ? 'text-red-500' : ''}>{value}</span>
    </div>
  );
}

export function RemontDetailSheet({ entry, vendors, open, onOpenChange }: RemontDetailSheetProps) {
  const [editOpen, setEditOpen] = useState(false);
  const historyQuery = useQuery({
    queryKey: ['service-center-repair-history', entry?.id],
    queryFn: () => serviceCenterRepairsApi.getHistory(entry!.id),
    enabled: open && !editOpen && !!entry?.id,
  });

  if (!entry) return null;

  const otvez = Number(entry.otvez) === 1;
  const zabral = Number(entry.zabral) === 1;
  const costNum = parseFloat(entry.cost) || 0;
  const hasAnswer = !!entry.answer?.trim();
  const vistavleno = Number(entry.vistavlenoklientu) === 1;
  const oplacheno = Number(entry.oplacheno) === 1;
  const isArchived = Number(entry.varhive) === 1;

  return (
    <>
      <Sheet open={open && !editOpen} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
          <SheetHeader className="text-left">
            <SheetTitle className="flex items-start gap-2">
              <Wrench className="h-5 w-5 shrink-0 mt-0.5" />
              <span className="break-words min-w-0">{entry.name}</span>
              {isArchived && (
                <Badge variant="secondary" className="text-xs shrink-0">
                  Архив
                </Badge>
              )}
            </SheetTitle>
            <SheetDescription>
              {entry.company}
              {entry.whose && ` (${entry.whose})`}
              {entry.remont_place && ` → ${entry.remont_place}`}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4 space-y-4">
            {/* Complaint */}
            {entry.comment && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm">
                  <LinkText>{entry.comment}</LinkText>
                </p>
              </div>
            )}

            {entry.diagnostics.length > 0 && (
              <div className="space-y-2">
                {entry.diagnostics.map((diagnostic) => (
                  <div
                    key={diagnostic.code}
                    className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800"
                  >
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    {diagnostic.message}
                  </div>
                ))}
              </div>
            )}

            {/* Photo */}
            {entry.photo && (
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <ImageIcon className="h-4 w-4" />
                  Фото
                </h4>
                <AuthImage filePath={entry.photo} alt="Устройство" />
              </div>
            )}

            {/* Status details */}
            <div className="rounded-lg border">
              <StatusRow label="Отвёз в СЦ" value={otvez ? 'Да' : 'Нет'} isYes={otvez} />
              <StatusRow label="Забрал из СЦ" value={zabral ? 'Да' : 'Нет'} isYes={zabral} />
              <StatusRow label="Стоимость" value={costNum > 0 ? `${costNum} руб.` : '—'} isYes={costNum > 0} />
              <StatusRow label="Ответ СЦ" value={hasAnswer ? entry.answer! : '—'} isYes={hasAnswer} />
              <StatusRow label="Выставлено клиенту" value={vistavleno ? 'Да' : 'Нет'} isYes={vistavleno} />
              <StatusRow label="Оплачено СЦ" value={oplacheno ? 'Да' : 'Нет'} isYes={oplacheno} />
              <StatusRow
                label="Без движения"
                value={`${entry.inactivityDays} дн.`}
                isYes={entry.attentionLevel === 'NONE'}
              />
            </div>

            {entry.raw.attachments.length > 0 && (
              <div className="rounded-lg border">
                {entry.raw.attachments.map((attachment) => (
                  <button
                    key={attachment.id}
                    type="button"
                    onClick={() => openAttachment(entry.id, attachment.id)}
                    className="flex w-full items-center gap-2 border-b px-3 py-2 text-left text-sm last:border-0"
                  >
                    <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate">
                      {attachment.originalName}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {ATTACHMENT_LABELS[attachment.kind]}
                    </span>
                    <ExternalLink className="h-4 w-4 shrink-0" />
                  </button>
                ))}
              </div>
            )}

            {/* Edit button */}
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setEditOpen(true)}
            >
              <Pencil className="h-4 w-4 mr-2" />
              Редактировать
            </Button>

            <MigratedCrudHistory
              legacyLog={entry.log}
              auditItems={historyQuery.data?.items || []}
              total={historyQuery.data?.total}
              isLoading={historyQuery.isLoading}
              error={historyQuery.error}
              fieldLabels={FIELD_LABELS}
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* Edit sheet */}
      <RemontSheet
        key={`${entry.id}-${editOpen}`}
        entry={entry}
        vendors={vendors}
        open={editOpen}
        onOpenChange={(isOpen) => {
          setEditOpen(isOpen);
          if (!isOpen) {
            // Close detail sheet too after editing
            onOpenChange(false);
          }
        }}
      />
    </>
  );
}
